import { execFile } from "node:child_process";
import { randomUUID } from "node:crypto";
import fs from "node:fs/promises";
import path from "node:path";
import { promisify } from "node:util";
import type {
  ChatMessage,
  ContentPart,
  ModelEvent,
  ModelInfo,
  ModelProvider,
  ToolDefinition,
} from "@mewbleh/nori-plugin-sdk";
import { AgentStore, type SharedStore } from "@mewbleh/nori-persistence";
import type { NoriConfig } from "./config.js";
import { resolveSecret } from "./config.js";
import { builtInProviders } from "./providers.js";
import { resolveOpenAiOAuthCredential } from "./openai-oauth.js";
import { McpClient, mcpToolDefinition, projectMcpToolName, toolAllowed } from "./mcp.js";
import { loadExtraBootstrapFiles } from "./hooks.js";
import { readWorkspaceSkill } from "./skills.js";
import { loadWorkspaceContext } from "./workspace.js";

const execFileAsync = promisify(execFile);

function boundedString(value: unknown, label: string, maxBytes: number): string {
  const text = String(value ?? "");
  if (Buffer.byteLength(text) > maxBytes) throw new Error(`${label} exceeds ${maxBytes} bytes`);
  return text;
}

export class ProviderRegistry {
  readonly providers = new Map<string, ModelProvider>();

  constructor(providers: Iterable<ModelProvider> = builtInProviders()) {
    for (const provider of providers) this.register(provider);
  }

  register(provider: ModelProvider): void {
    if (this.providers.has(provider.id)) throw new Error(`Provider already registered: ${provider.id}`);
    this.providers.set(provider.id, provider);
  }

  get(id: string): ModelProvider {
    const provider = this.providers.get(id);
    if (!provider) throw new Error(`Unknown model provider: ${id}`);
    return provider;
  }
}

export type ToolHandler = {
  definition: ToolDefinition;
  group: "memory" | "filesystem" | "shell" | "skills" | "plugins" | "mcp";
  risk: "low" | "medium" | "high";
  approvalKind?: string;
  approvalSummary?(input: unknown): string;
  execute(input: unknown, context?: { store: AgentStore; workspace: string }): Promise<unknown>;
};

export type ToolInvocationResult =
  | { ok: true; toolName: string; output: unknown; source: "core" | "mcp" }
  | { ok: false; toolName: string; requiresApproval?: true; approvalId?: string; idempotencyKey?: string; error: { code: string; message: string } };

type RuntimeMedia = { url?: string; data?: Uint8Array | string; mediaType: string; name?: string };

function userContent(text: string, media: readonly RuntimeMedia[] | undefined): ChatMessage["content"] {
  if (!media?.length) return text;
  const parts: ContentPart[] = text ? [{ type: "text", text }] : [];
  for (const item of media) {
    const encoded = item.data instanceof Uint8Array ? Buffer.from(item.data).toString("base64") : item.data;
    if (item.mediaType.startsWith("image/")) {
      parts.push({
        type: "image",
        mediaType: item.mediaType,
        ...(item.url ? { url: item.url } : {}),
        ...(encoded ? { data: encoded } : {}),
      });
    } else {
      parts.push({
        type: "file",
        mediaType: item.mediaType,
        ...(item.name ? { name: item.name } : {}),
        ...(item.url ? { url: item.url } : {}),
        ...(encoded ? { data: encoded } : {}),
      });
    }
  }
  return parts;
}

function transcriptContent(value: unknown): ChatMessage["content"] {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value as ContentPart[];
  return JSON.stringify(value);
}

export class AgentRuntime {
  readonly #sessionTails = new Map<string, Promise<void>>();
  readonly #activeRuns = new Map<string, AbortController>();
  readonly #mcpClients = new Map<string, McpClient>();
  readonly #mcpState = new Map<string, { connected: boolean; tools: string[]; error?: string }>();
  readonly tools = new Map<string, ToolHandler>();

  constructor(
    readonly config: NoriConfig,
    readonly store: AgentStore,
    readonly providers = new ProviderRegistry(),
    readonly env: NodeJS.ProcessEnv = process.env,
    readonly approvals?: SharedStore,
    readonly storeResolver?: (agentId: string) => AgentStore,
  ) {
    this.registerSafeMemoryTools();
    this.registerApprovedTools();
  }

  async initializeMcp(): Promise<void> {
    for (const [name, server] of Object.entries(this.config.mcp.servers)) {
      if (server.enabled === false) {
        this.#mcpState.set(name, { connected: false, tools: [] });
        continue;
      }
      try {
        const client = await McpClient.connect(name, server, { env: this.env });
        const tools = (await client.listTools()).filter((tool) => toolAllowed(server, tool.name));
        for (const tool of tools) {
          const projectedName = projectMcpToolName(name, tool.name);
          if (this.tools.has(projectedName)) throw new Error(`Projected MCP tool collides with an existing tool: ${projectedName}`);
          this.tools.set(projectedName, {
            group: "mcp",
            risk: "high",
            approvalKind: `mcp.${name}.${tool.name}`,
            approvalSummary: () => `Call MCP tool ${name}/${tool.name}`,
            definition: mcpToolDefinition(name, tool),
            execute: async (input) => client.callTool(tool.name, input),
          });
        }
        this.#mcpClients.set(name, client);
        this.#mcpState.set(name, { connected: true, tools: tools.map((tool) => tool.name) });
      } catch (error) {
        this.#mcpState.set(name, { connected: false, tools: [], error: (error as Error).message });
      }
    }
  }

  async reloadMcp(): Promise<void> {
    for (const [name, client] of this.#mcpClients) {
      client.close();
      this.#mcpClients.delete(name);
      for (const toolName of [...this.tools.keys()]) if (toolName.startsWith(`${name}__`)) this.tools.delete(toolName);
    }
    this.#mcpState.clear();
    await this.initializeMcp();
  }

  mcpStatus(): Array<{ name: string; configured: boolean; connected: boolean; tools: string[]; error?: string }> {
    return Object.keys(this.config.mcp.servers).map((name) => {
      const state = this.#mcpState.get(name);
      return { name, configured: true, connected: state?.connected ?? false, tools: state?.tools ?? [], ...(state?.error ? { error: state.error } : {}) };
    });
  }

  close(): void {
    for (const client of this.#mcpClients.values()) client.close();
    this.#mcpClients.clear();
  }

  registerSafeMemoryTools(): void {
    this.tools.set("memory_search", {
      group: "memory",
      risk: "low",
      definition: {
        name: "memory_search",
        description: "Search Nori's durable memory for user-relevant information.",
        inputSchema: { type: "object", properties: { query: { type: "string" } }, required: ["query"], additionalProperties: false },
      },
      execute: async (input, context) => (context?.store ?? this.store).searchMemory(boundedString((input as { query?: unknown })?.query, "Memory query", 32 * 1024)),
    });
    this.tools.set("memory_remember", {
      group: "memory",
      risk: "low",
      definition: {
        name: "memory_remember",
        description: "Remember a durable, non-secret fact that the user explicitly asked Nori to retain.",
        inputSchema: { type: "object", properties: { content: { type: "string" } }, required: ["content"], additionalProperties: false },
      },
      execute: async (input, context) => ({ id: (context?.store ?? this.store).putMemory(boundedString((input as { content?: unknown })?.content, "Memory content", 128 * 1024), { source: "agent" }) }),
    });
    this.tools.set("skill_read", {
      group: "skills",
      risk: "low",
      definition: {
        name: "skill_read",
        description: "Read one explicitly installed skill from Nori's current workspace.",
        inputSchema: { type: "object", properties: { key: { type: "string" } }, required: ["key"], additionalProperties: false },
      },
      execute: async (input, context) => readWorkspaceSkill(context?.workspace ?? this.config.agents.defaults.workspace, String((input as { key?: unknown })?.key ?? "")),
    });
  }

  async #requireApproval(kind: string, summary: string, context: Record<string, unknown>): Promise<void> {
    if (!this.approvals) throw new Error(`${kind} requires an approval store`);
    const id = this.approvals.createApproval(kind, summary, context);
    const deadline = Date.now() + 5 * 60_000;
    while (Date.now() < deadline) {
      const approval = this.approvals.getApproval(id);
      if (approval?.status === "approved") {
        if (!this.approvals.consumeApproval(id)) throw new Error(`Approval is no longer usable: ${id}`);
        return;
      }
      if (approval?.status === "denied") throw new Error(`Operator denied ${kind} approval ${id}`);
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    throw new Error(`Approval timed out: ${id}`);
  }

  async #workspacePath(workspacePath: string, input: unknown, forWrite = false): Promise<string> {
    const candidate = String(input ?? "");
    if (!candidate || path.isAbsolute(candidate)) throw new Error("Tool paths must be relative to the configured workspace");
    if (Buffer.byteLength(candidate) > 4_096) throw new Error("Tool path is too long");
    const workspace = await fs.realpath(workspacePath);
    const resolved = path.resolve(workspace, candidate);
    if (resolved !== workspace && !resolved.startsWith(`${workspace}${path.sep}`)) throw new Error("Tool path escapes the workspace");
    if (forWrite) {
      await fs.mkdir(path.dirname(resolved), { recursive: true, mode: 0o700 });
      const parent = await fs.realpath(path.dirname(resolved));
      if (parent !== workspace && !parent.startsWith(`${workspace}${path.sep}`)) throw new Error("Tool path traverses a symlink outside the workspace");
      return path.join(parent, path.basename(resolved));
    }
    const real = await fs.realpath(resolved);
    if (real !== workspace && !real.startsWith(`${workspace}${path.sep}`)) throw new Error("Tool path traverses a symlink outside the workspace");
    return real;
  }

  registerApprovedTools(): void {
    if (this.config.tools.filesystem) {
      this.tools.set("filesystem_read", {
        group: "filesystem",
        risk: "medium",
        approvalKind: "filesystem.read",
        approvalSummary: (input) => `Read workspace file ${String((input as { path?: unknown })?.path ?? "")}`,
        definition: {
          name: "filesystem_read",
          description: "Read a UTF-8 file inside Nori's workspace after operator approval.",
          inputSchema: { type: "object", properties: { path: { type: "string" } }, required: ["path"], additionalProperties: false },
        },
        execute: async (input, context) => {
          const relativePath = String((input as { path?: unknown })?.path ?? "");
          return { content: (await fs.readFile(await this.#workspacePath(context?.workspace ?? this.config.agents.defaults.workspace, relativePath), "utf8")).slice(0, 128 * 1024) };
        },
      });
      this.tools.set("filesystem_write", {
        group: "filesystem",
        risk: "high",
        approvalKind: "filesystem.write",
        approvalSummary: (input) => `Write workspace file ${String((input as { path?: unknown })?.path ?? "")}`,
        definition: {
          name: "filesystem_write",
          description: "Write a UTF-8 file inside Nori's workspace after operator approval.",
          inputSchema: {
            type: "object",
            properties: { path: { type: "string" }, content: { type: "string" } },
            required: ["path", "content"],
            additionalProperties: false,
          },
        },
        execute: async (input, context) => {
          const request = input as { path?: unknown; content?: unknown };
          const relativePath = String(request?.path ?? "");
          const content = boundedString(request?.content, "Filesystem content", 1024 * 1024);
          const destination = await this.#workspacePath(context?.workspace ?? this.config.agents.defaults.workspace, relativePath, true);
          await fs.writeFile(destination, content, { mode: 0o600 });
          return { path: relativePath, bytes: Buffer.byteLength(content) };
        },
      });
    }
    if (this.config.tools.shell) {
      this.tools.set("shell", {
        group: "shell",
        risk: "high",
        approvalKind: "shell",
        approvalSummary: (input) => `Run shell command: ${String((input as { command?: unknown })?.command ?? "").slice(0, 160)}`,
        definition: {
          name: "shell",
          description: "Run a command in Nori's workspace after explicit operator approval.",
          inputSchema: { type: "object", properties: { command: { type: "string" } }, required: ["command"], additionalProperties: false },
        },
        execute: async (input, context) => {
          const command = boundedString((input as { command?: unknown })?.command, "Shell command", 64 * 1024);
          if (!command) throw new Error("Shell command is required");
          const shell = process.platform === "win32" ? process.env.ComSpec ?? "cmd.exe" : "/bin/sh";
          const args = process.platform === "win32" ? ["/d", "/s", "/c", command] : ["-lc", command];
          const result = await execFileAsync(shell, args, { cwd: context?.workspace ?? this.config.agents.defaults.workspace, timeout: 60_000, maxBuffer: 1024 * 1024 });
          return { stdout: result.stdout.slice(0, 128 * 1024), stderr: result.stderr.slice(0, 128 * 1024) };
        },
      });
    }
  }

  toolCatalog(agentId = "main") {
    const groups = new Map<string, Array<Record<string, unknown>>>();
    for (const handler of this.tools.values()) {
      const tools = groups.get(handler.group) ?? [];
      tools.push({
        id: handler.definition.name,
        label: handler.definition.name,
        description: handler.definition.description,
        source: "core",
        risk: handler.risk,
        defaultProfiles: handler.risk === "low" ? ["minimal", "coding", "messaging", "full"] : ["full"],
      });
      groups.set(handler.group, tools);
    }
    return {
      agentId,
      profiles: ["minimal", "coding", "messaging", "full"].map((id) => ({ id, label: id[0]!.toUpperCase() + id.slice(1) })),
      groups: [...groups.entries()].map(([id, tools]) => ({ id, label: id[0]!.toUpperCase() + id.slice(1), source: "core", tools })),
    };
  }

  effectiveTools(agentId: string, sessionKey: string) {
    return {
      agentId,
      sessionKey,
      profile: "full",
      groups: this.toolCatalog(agentId).groups.map((group) => ({
        ...group,
        tools: group.tools.map((tool) => ({ ...tool, enabled: true })),
      })),
      notices: [],
    };
  }

  async invokeTool(params: { name: string; args?: unknown; sessionKey?: string; agentId?: string; confirm?: boolean; idempotencyKey?: string }): Promise<ToolInvocationResult> {
    const handler = this.tools.get(params.name);
    if (!handler) return { ok: false, toolName: params.name, error: { code: "not_found", message: `Unknown tool: ${params.name}` } };
    const agentId = params.agentId ?? "main";
    const store = this.storeResolver?.(agentId) ?? this.store;
    const agent = this.config.agents.list.find((entry) => entry.id === agentId);
    if (!agent) return { ok: false, toolName: params.name, error: { code: "not_found", message: `Unknown agent: ${agentId}` } };
    const workspace = agent.workspace ?? this.config.agents.defaults.workspace;
    const args = params.args ?? {};
    if (handler.approvalKind) {
      if (!this.approvals) return { ok: false, toolName: params.name, error: { code: "forbidden", message: `${params.name} requires an approval store` } };
      if (!params.confirm) {
        return { ok: false, toolName: params.name, requiresApproval: true, error: { code: "requires_approval", message: `${params.name} requires operator approval` } };
      }
      const idempotencyKey = params.idempotencyKey ?? randomUUID();
      const approvalKey = `tool:${agentId}:${params.sessionKey ?? "gateway"}:${params.name}:${idempotencyKey}`;
      const approvalId = this.approvals.createApproval(
        handler.approvalKind,
        handler.approvalSummary?.(args) ?? `Invoke ${params.name}`,
        { agentId, sessionKey: params.sessionKey, tool: params.name, args },
        approvalKey,
      );
      const approval = this.approvals.getApprovalByKey(approvalKey);
      if (approval?.status === "denied") return { ok: false, toolName: params.name, error: { code: "forbidden", message: `Operator denied approval ${approvalId}` } };
      if (approval?.status !== "approved") {
        return { ok: false, toolName: params.name, requiresApproval: true, approvalId, idempotencyKey, error: { code: "requires_approval", message: `Approval pending: ${approvalId}` } };
      }
      if (!this.approvals.consumeApproval(approvalId)) return { ok: false, toolName: params.name, error: { code: "forbidden", message: `Approval is no longer usable: ${approvalId}` } };
    }
    try {
      return { ok: true, toolName: params.name, output: await handler.execute(args, { store, workspace }), source: handler.group === "mcp" ? "mcp" : "core" };
    } catch (error) {
      return { ok: false, toolName: params.name, error: { code: "tool_error", message: (error as Error).message } };
    }
  }

  abort(sessionKey: string): boolean {
    const controller = this.#activeRuns.get(sessionKey);
    if (!controller) return false;
    controller.abort();
    return true;
  }

  async #acquire(sessionKey: string): Promise<() => void> {
    const previous = this.#sessionTails.get(sessionKey) ?? Promise.resolve();
    let release!: () => void;
    const current = new Promise<void>((resolve) => {
      release = resolve;
    });
    const tail = previous.then(() => current);
    this.#sessionTails.set(sessionKey, tail);
    await previous;
    return () => {
      release();
      if (this.#sessionTails.get(sessionKey) === tail) this.#sessionTails.delete(sessionKey);
    };
  }

  async #providerContext(providerId: string, signal: AbortSignal, store = this.store) {
    const providerConfig = this.config.providers[providerId] ?? {};
    const envNames: Record<string, string> = {
      openai: "OPENAI_API_KEY",
      anthropic: "ANTHROPIC_API_KEY",
      openrouter: "OPENROUTER_API_KEY",
      ollama: "OLLAMA_API_KEY",
      cerebras: "CEREBRAS_API_KEY",
      deepinfra: "DEEPINFRA_API_KEY",
      deepseek: "DEEPSEEK_API_KEY",
      featherless: "FEATHERLESS_API_KEY",
      fireworks: "FIREWORKS_API_KEY",
      groq: "GROQ_API_KEY",
      huggingface: "HF_TOKEN",
      lmstudio: "LMSTUDIO_API_KEY",
      mistral: "MISTRAL_API_KEY",
      nvidia: "NVIDIA_API_KEY",
      together: "TOGETHER_API_KEY",
      venice: "VENICE_API_KEY",
      xai: "XAI_API_KEY",
      vllm: "VLLM_API_KEY",
    };
    const configuredApiKey =
      (envNames[providerId] ? this.env[envNames[providerId]] : undefined) ??
      (await resolveSecret(providerConfig.apiKey, this.env)) ??
      store.getCredential(providerId);
    const apiKey = providerConfig.authMode === "oauth" ? undefined : configuredApiKey;
    const oauth = providerId === "openai" && providerConfig.authMode !== "api-key" && !apiKey
      ? await resolveOpenAiOAuthCredential(store, { fetch: globalThis.fetch, signal })
      : undefined;
    const configuredModels = [
      this.config.agents.defaults.model.primary,
      ...this.config.agents.defaults.model.fallbacks,
      ...this.config.agents.list.map((agent) => agent.model),
    ].filter((model): model is string => Boolean(model?.startsWith(`${providerId}/`))).map((model) => model.slice(providerId.length + 1));
    return {
      signal,
      fetch: globalThis.fetch,
      ...(apiKey ? { apiKey } : {}),
      ...(apiKey ? { auth: { type: "api-key" as const } } : {}),
      ...(oauth ? { auth: { type: "oauth" as const, accessToken: oauth.accessToken, accountId: oauth.accountId } } : {}),
      ...(providerConfig.baseUrl ? { baseUrl: providerConfig.baseUrl } : {}),
      ...(providerConfig.oauthBaseUrl ? { oauthBaseUrl: providerConfig.oauthBaseUrl } : {}),
      ...(configuredModels.length ? { configuredModels: [...new Set(configuredModels)] } : {}),
    };
  }

  async listModels(providerId: string): Promise<ModelInfo[]> {
    const controller = new AbortController();
    let timeout: NodeJS.Timeout | undefined;
    try {
      const context = await this.#providerContext(providerId, controller.signal);
      const { signal: _signal, ...discoveryContext } = context;
      return await Promise.race([
        this.providers.get(providerId).listModels(discoveryContext),
        new Promise<never>((_resolve, reject) => {
          timeout = setTimeout(() => {
            controller.abort();
            reject(new Error(`Model discovery timed out for provider: ${providerId}`));
          }, 15_000);
        }),
      ]);
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }

  async probeProvider(providerId: string): Promise<{ ok: boolean; message?: string }> {
    const controller = new AbortController();
    try {
      const context = await this.#providerContext(providerId, controller.signal);
      const { signal: _signal, ...probeContext } = context;
      const provider = this.providers.get(providerId);
      if (provider.probe) {
        let timeout: NodeJS.Timeout | undefined;
        try {
          return await Promise.race([
            provider.probe(probeContext),
            new Promise<never>((_resolve, reject) => {
              timeout = setTimeout(() => reject(new Error(`Provider probe timed out: ${providerId}`)), 15_000);
            }),
          ]);
        } finally {
          if (timeout) clearTimeout(timeout);
        }
      }
      const models = await this.listModels(providerId);
      return { ok: true, message: `${models.length} model${models.length === 1 ? "" : "s"} available` };
    } catch (error) {
      return { ok: false, message: (error as Error).message };
    }
  }

  async *run(params: {
    text: string;
    sessionKey: string;
    agentId?: string;
    channel?: string;
    peerId?: string;
    model?: string;
    media?: RuntimeMedia[];
  }): AsyncIterable<ModelEvent> {
    if (Buffer.byteLength(params.text) > 1024 * 1024) throw new Error("Agent input exceeds 1 MiB");
    if (!params.sessionKey || Buffer.byteLength(params.sessionKey) > 512) throw new Error("Session key must be 1..512 bytes");
    if ((params.media?.length ?? 0) > 16) throw new Error("Agent input supports at most 16 media items");
    let mediaBytes = 0;
    for (const item of params.media ?? []) {
      mediaBytes += typeof item.data === "string" ? Buffer.byteLength(item.data) : item.data?.byteLength ?? 0;
      if (item.url && Buffer.byteLength(item.url) > 16 * 1024) throw new Error("Media URL is too long");
    }
    if (mediaBytes > 20 * 1024 * 1024) throw new Error("Agent media exceeds 20 MiB");
    const release = await this.#acquire(params.sessionKey);
    const controller = new AbortController();
    this.#activeRuns.set(params.sessionKey, controller);
    try {
      const agentId = params.agentId ?? "main";
      const store = this.storeResolver?.(agentId) ?? this.store;
      const agentConfig = this.config.agents.list.find((agent) => agent.id === agentId);
      const workspace = agentConfig?.workspace ?? this.config.agents.defaults.workspace;
      const primaryModel = params.model ?? agentConfig?.model ?? this.config.agents.defaults.model.primary;
      const modelRefs = [primaryModel, ...this.config.agents.defaults.model.fallbacks].filter(
        (entry, index, all): entry is string => Boolean(entry) && all.indexOf(entry) === index,
      );
      if (!modelRefs.length || modelRefs.some((entry) => !entry.includes("/"))) {
        throw new Error("Configure models as provider/model before running Nori");
      }
      const session = store.ensureSession({
        sessionKey: params.sessionKey,
        agentId,
        ...(params.channel ? { channel: params.channel } : {}),
        ...(params.peerId ? { peerId: params.peerId } : {}),
      });
      store.appendTranscript(session.sessionId, "user", userContent(params.text, params.media));
      const history = store.history(session.sessionId, 80);
      const context = await loadWorkspaceContext(workspace);
      const extraBootstrap = await loadExtraBootstrapFiles(workspace, this.config);
      const memory = store.searchMemory(params.text, 5);
      const system = `${context}${extraBootstrap.length ? `\n\n## Extra bootstrap files\n\n${extraBootstrap.map((entry) => `### ${entry.path}\n\n${entry.content.trim()}`).join("\n\n")}` : ""}\n\n## Relevant memory\n${memory.length ? memory.map((entry) => `- ${entry.content}`).join("\n") : "No matching durable memory."}`;
      const messages: ChatMessage[] = [
        { role: "system", content: system },
        ...(history.length > 60
          ? [{ role: "system" as const, content: `${history.length - 60} older transcript events were compacted from this request.` }]
          : []),
        ...history.slice(-60).map((entry): ChatMessage => ({
          role: entry.role === "assistant" || entry.role === "tool" || entry.role === "system" ? entry.role : "user",
          content: transcriptContent(entry.content),
        })),
      ];
      let finalText = "";
      let outputBytes = 0;
      let usage = { inputTokens: 0, outputTokens: 0 };
      for (let round = 0; round < 4; round += 1) {
        const toolCalls: Array<{ id: string; name: string; arguments: string }> = [];
        let roundText = "";
        let streamError: unknown;
        let completed = false;
        for (const modelRef of modelRefs) {
          const [providerId, ...modelParts] = modelRef.split("/");
          const provider = this.providers.get(providerId!);
          const providerContext = await this.#providerContext(providerId!, controller.signal, store);
          let emittedContent = false;
          try {
            for await (const event of provider.stream(
              { model: modelParts.join("/"), messages, tools: [...this.tools.values()].map((tool) => tool.definition) },
              providerContext,
            )) {
              if (controller.signal.aborted) {
                yield { type: "finish", reason: "cancelled" };
                return;
              }
              if (event.type === "text-delta") {
                emittedContent = true;
                roundText += event.text;
                finalText += event.text;
                outputBytes += Buffer.byteLength(event.text);
                if (outputBytes > 8 * 1024 * 1024) throw new Error("Model output exceeds 8 MiB");
              } else if (event.type === "tool-call") {
                emittedContent = true;
                toolCalls.push(event);
              } else if (event.type === "usage") {
                usage.inputTokens += event.usage.inputTokens;
                usage.outputTokens += event.usage.outputTokens;
              } else if (event.type === "error") {
                throw Object.assign(new Error(event.message), { retryable: event.retryable });
              }
              yield event;
            }
            completed = true;
            break;
          } catch (error) {
            streamError = error;
            if (emittedContent || modelRef === modelRefs.at(-1)) throw error;
          }
        }
        if (!completed) throw streamError ?? new Error("All configured models failed");
        if (!toolCalls.length) break;
        messages.push({ role: "assistant", content: roundText, toolCalls });
        for (const call of toolCalls) {
          const handler = this.tools.get(call.name);
          if (!handler) throw new Error(`Model requested unavailable tool: ${call.name}`);
          let input: unknown;
          try {
            input = JSON.parse(call.arguments || "{}");
          } catch {
            input = { raw: call.arguments };
          }
          if (handler.approvalKind) {
            await this.#requireApproval(
              handler.approvalKind,
              handler.approvalSummary?.(input) ?? `Invoke ${handler.definition.name}`,
              { agentId, sessionKey: params.sessionKey, tool: handler.definition.name, args: input },
            );
          }
          const result = await handler.execute(input, { store, workspace });
          messages.push({ role: "tool", name: call.name, toolCallId: call.id, content: JSON.stringify(result) });
          store.appendTranscript(session.sessionId, "tool", { name: call.name, input, result });
        }
      }
      if (finalText) store.appendTranscript(session.sessionId, "assistant", finalText);
      store.addUsage(session.sessionId, usage.inputTokens, usage.outputTokens);
    } catch (error) {
      if (controller.signal.aborted) {
        yield { type: "finish", reason: "cancelled" };
      } else {
        const candidate = error as Error & { retryable?: boolean; status?: number };
        yield {
          type: "error",
          code: candidate.status ? `HTTP_${candidate.status}` : "AGENT_ERROR",
          message: candidate.message,
          retryable: candidate.retryable ?? false,
        };
      }
    } finally {
      this.#activeRuns.delete(params.sessionKey);
      release();
    }
  }
}
